"use client";

import { uploadFilesToDB } from "@/actions/apiClientActions/files";
import Uppy from "@uppy/core";
import XHRUpload from "@uppy/xhr-upload";
import { Dashboard } from "@uppy/react";

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { useErrorToast } from "@/lib/hooks";
import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";
import { useTheme } from "next-themes";
import { API_ENDPOINTS } from "@/app/assets/data/apiEndPointData";
import {
  getUppyFileTypeCategory,
  MAX_UPPY_FILES_SIZES_LIMITS,
} from "@/lib/uppy/helper";

// Current max 50 files, 1000 MB
const createUppy = (userId) => {
  const uppyInstance = new Uppy({
    id: "uppy-local-uploader",
    restrictions: {
      maxNumberOfFiles: parseInt(
        process.env.NEXT_PUBLIC_UPPY_RESTRICTED_FILES_COUNT || "10"
      ),
      // maxTotalFileSize:
      //   parseInt(process.env.NEXT_PUBLIC_UPPY_RESTRICTED_FILES_SIZE || "100") *
      //   1024 *
      //   1024,
    },
    autoProceed: false,
  });

  // Restrict Sizes Based on File Types
  uppyInstance.on("file-added", (file) => {
    let customType;
    let maxAllowedSize;

    customType = getUppyFileTypeCategory(file.type);
    maxAllowedSize = MAX_UPPY_FILES_SIZES_LIMITS[customType];

    // console.log("type: ", file.type);
    // console.log("customType: ", customType);
    // console.log("maxAllowedSize: ", maxAllowedSize);

    if (file.size > maxAllowedSize) {
      uppyInstance.info(
        `${file.name} is too large. Max allowed size: ${Math.round(
          maxAllowedSize / (1024 * 1024)
        )} MB`,
        "error",
        5000
      );
      uppyInstance.removeFile(file.id);
    }
  });

  // Create File In Locale
  uppyInstance.use(XHRUpload, {
    endpoint: `${process.env.NEXT_PUBLIC_DOMAIN_URL}${API_ENDPOINTS.LOCAL_FILE_UPLOAD}/${userId}`, // Your local API route
    fieldName: "files", // Field name used in FormData
    bundle: true,
  });

  return uppyInstance;
};

const UppyLocalUploader = ({ userId }) => {
  const [uppy] = useState(() => createUppy(userId));
  const toastIdRef = useRef(null);
  const { theme } = useTheme();

  useEffect(() => {
    const handleComplete = async (result) => {
      const { successful = [], failed } = result;

      const validUploads = successful.map((file, index) => {
        return {
          userId,
          name: file.name,
          key: file?.response?.body?.files?.[index]?.fileKey || "N/A",
          size: file.size,
          type: file.type,
          url: file?.response?.body?.files?.[index]?.fileKey || "N/A",
        };
      });

      for (let file of validUploads) {
        const { success, message } = await uploadFilesToDB(file);
        if (!success) {
          if (toastIdRef.current) {
            toast.dismiss(toastIdRef.current);
            toast.error(message);
          } else {
            toast.error(message);
          }
        } else {
          if (toastIdRef.current) {
            toast.dismiss(toastIdRef.current);
            toast.success(message);
          } else {
            toast.success(message);
          }
        }
      }
    };

    const handleProgress = (progress) => {
      const progressMessage = `Uploading files... progress: ${progress}/100%`;

      if (!toastIdRef.current && progress > 0) {
        toastIdRef.current = toast.loading(progressMessage, {
          action: {
            label: "Cancel",
            onClick: () => {
              console.log("Close");
            },
          },
        });
      } else if (progress > 0) {
        toast.loading(progressMessage, {
          id: toastIdRef.current,
        });
      }
    };

    uppy.on("complete", handleComplete);
    uppy.on("progress", handleProgress);

    return () => {
      // Cleanup listeners and destroy instance only on unmount
      uppy.off("complete", handleComplete);
      uppy.off("progress", handleProgress);
    };
  }, [uppy, userId]);

  return (
    <Dashboard
      uppy={uppy}
      theme={theme}
      height={300} // Example height
      showProgressDetails
      note="Drag and drop your files here or browse to upload"
      inline={true}
      width="100%" // Full width
      proudlyDisplayPoweredByUppy={false}
      showLinkToFileUploadResult={false}
      showRemoveButtonAfterComplete={false}
    />
  );
};

export default UppyLocalUploader;
